import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon, CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Block } from '../types';
import { VersionEvent } from '../types/Version';

interface VersionEventMatrixProps {
  versionName: string;
  events: VersionEvent[];
  lots: Block[];
  pendingCount: number;
  onClose: () => void;
  onPointEvent: (eventId: string, lotId: string) => void;
  onAddEvent: (name: string) => void;
  onFinalizeVersion: () => void;
}

function isEventApplicable(event: VersionEvent, lotId: string): boolean {
  return event.lotIds === null || event.lotIds.includes(lotId);
}

const VersionEventMatrix: React.FC<VersionEventMatrixProps> = ({
  versionName,
  events,
  lots,
  pendingCount,
  onClose,
  onPointEvent,
  onAddEvent,
  onFinalizeVersion,
}) => {
  const [quickName, setQuickName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus l'input à l'ouverture du panneau
  useEffect(() => {
    if (events.length === 0) {
      inputRef.current?.focus();
    }
  }, [events.length]);

  const handleQuickAdd = () => {
    const name = quickName.trim();
    if (!name) return;
    onAddEvent(name);
    setQuickName('');
    inputRef.current?.focus();
  };

  const canFinalize = pendingCount === 0 && events.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <span className="text-sm font-semibold text-gray-700">Pointages</span>
        <span className="text-xs text-gray-400 font-medium">{versionName}</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          {lots.length > 0 && (
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/70">
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                  Événement
                </th>
                {lots.map(lot => (
                  <th
                    key={lot.id}
                    className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <span className="block truncate max-w-[96px] mx-auto" title={lot.content.titre}>
                      {lot.content.titre || '—'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className="divide-y divide-gray-100">
            {events.map(event => (
              <tr key={event.id} className="hover:bg-gray-50/60 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800">{event.name}</div>
                  {event.description && (
                    <div className="text-xs text-gray-400 mt-0.5 line-clamp-1">{event.description}</div>
                  )}
                </td>
                {lots.map(lot => {
                  const applicable = isEventApplicable(event, lot.id);
                  if (!applicable) {
                    return <td key={lot.id} className="px-3 py-3 text-center text-gray-300">—</td>;
                  }
                  const pointed = !!event.pointages[lot.id];
                  return (
                    <td key={lot.id} className="px-3 py-3 text-center">
                      {pointed ? (
                        <span
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-600"
                          title="Pointé"
                        >
                          <CheckIcon className="w-3.5 h-3.5" />
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onPointEvent(event.id, lot.id)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded border-2 border-gray-200 text-gray-300 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                          title="Pointer"
                        >
                          <span className="w-2 h-2 rounded-sm bg-current opacity-30" />
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}

            {/* Ligne quick-add inline */}
            <tr className="bg-gray-50/40">
              <td className="px-3 py-2" colSpan={lots.length + 1}>
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={quickName}
                    onChange={e => setQuickName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleQuickAdd();
                      if (e.key === 'Escape') setQuickName('');
                    }}
                    placeholder="Nouvel événement… (Entrée pour ajouter)"
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent bg-white placeholder-gray-300"
                  />
                  <button
                    type="button"
                    onClick={handleQuickAdd}
                    disabled={!quickName.trim()}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    <PlusIcon className="w-3 h-3" />
                    Ajouter
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Footer — Finaliser */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
        <span className="text-xs text-gray-400">
          {pendingCount > 0
            ? `${pendingCount} pointage${pendingCount > 1 ? 's' : ''} en attente`
            : events.length === 0
            ? 'Aucun événement'
            : 'Tous les pointages sont à jour ✅'}
        </span>
        <button
          type="button"
          onClick={canFinalize ? onFinalizeVersion : undefined}
          disabled={!canFinalize}
          title={!canFinalize && pendingCount > 0 ? `${pendingCount} pointage${pendingCount > 1 ? 's' : ''} en attente` : undefined}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            canFinalize
              ? 'bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          Finaliser la version
        </button>
      </div>
    </div>
  );
};

export default VersionEventMatrix;
